import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Container, Row, Col, Button, Table, Badge,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import toastr from "toastr";
import { downloadInvoicePDF } from "./InvoicePDF";

const BillingHistory = () => {
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Billing History", link: "#" },
  ];

  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(null); // key of item being downloaded

  useEffect(() => {
    loadHistory();
  }, []);

  function loadHistory() {
    try {
      const raw = localStorage.getItem("invoiceHistory");
      setHistory(raw ? JSON.parse(raw) : []);
    } catch (e) {
      console.error("Failed to load invoice history", e);
      setHistory([]);
    }
  }

  // Re-generate PDF for a history entry
  const handleRedownload = async (entry) => {
    // Try new format (invoiceData stored directly in history entry)
    let invoiceData = entry.invoiceData || null;

    // Fallback: load from localStorage key (old format)
    if (!invoiceData && entry.key) {
      try {
        const raw = localStorage.getItem(entry.key);
        if (raw) {
          const stored = JSON.parse(raw);
          // New format: { invoiceData }
          if (stored.invoiceData) {
            invoiceData = stored.invoiceData;
          } else if (stored.customer && stored.meta) {
            // Old format: { customer, meta }
            const { customer, meta } = stored;
            invoiceData = {
              invoiceNo: meta.invoiceNo,
              invoiceDate: meta.invoiceDate,
              buyerOrderNo: meta.buyerOrderNo || `S-${meta.invoiceNo}`,
              refNo: meta.refNo || "",
              customerName: customer.name || "",
              phone: customer.phone || "",
              customerAddress: customer.address || "",
              items: (customer.orders || []).map((o) => ({
                description: o.vehicle_no || "HIGH SPEED DIESEL BS VI LTR",
                hsn: "27101944",
                qty: o.total_qty || 0,
                rate: o.price_per_unit || 0,
              })),
            };
          }
        }
      } catch (e) {
        console.error("Failed to load stored invoice", e);
      }
    }

    if (!invoiceData) {
      toastr.error("Invoice data not found. Cannot re-download.");
      return;
    }

    setDownloading(entry.key);
    try {
      await downloadInvoicePDF(invoiceData);
      toastr.success(`Invoice ${entry.invoiceNo} downloaded!`);
    } catch (err) {
      console.error("Re-download failed:", err);
      toastr.error("Failed to generate PDF.");
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = (idx) => {
    const copy = [...history];
    const removed = copy.splice(idx, 1)[0];
    setHistory(copy);
    localStorage.setItem("invoiceHistory", JSON.stringify(copy));
    try { localStorage.removeItem(removed.key); } catch (_) {}
  };

  const fmtAmt = (amt) =>
    amt != null && !isNaN(amt)
      ? "Rs. " + parseFloat(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "-";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Billing History" breadcrumbItems={breadcrumbItems} />
        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col>
                <h5 className="mb-0">
                  All Generated Bills{" "}
                  {history.length > 0 && (
                    <Badge color="secondary" pill>{history.length}</Badge>
                  )}
                </h5>
              </Col>
            </Row>

            {history.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="mdi mdi-file-document-outline" style={{ fontSize: 48 }} />
                <p className="mt-2">No bills generated yet.</p>
              </div>
            ) : (
              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Invoice No.</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={h.key || i}>
                      <td>{i + 1}</td>
                      <td>
                        <strong>{h.invoiceNo}</strong>
                      </td>
                      <td>{h.customerName || "-"}</td>
                      <td>{h.date || "-"}</td>
                      <td>{fmtAmt(h.amount)}</td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          className="me-2"
                          disabled={downloading === h.key}
                          onClick={() => handleRedownload(h)}
                        >
                          {downloading === h.key
                            ? "Generating..."
                            : "Re-download PDF"}
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => handleDelete(i)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default BillingHistory;
